export const ORIGIN_LABELS = {
  profile: 'Profile (Referral Hub)',
  fancam: 'FanCam',
  tiri_rateit: 'TIRI – Rate It',
  unknown: 'Unknown / legacy'
};

export const MODULE_LABELS = { fancam: 'FanCam', rateit: 'TIRI – Rate It' };

export const STATUS_LABELS = { pending: 'In progress', completed: 'Completed' };

export const KIND_LABELS = {
  team: 'Team-wide',
  post: 'Single post',
  manual: 'Share button',
  milestone: 'Milestone invite',
  unknown: '—'
};

export const fmtDate = (value) => (value ? new Date(value).toLocaleString() : '—');

const inputClass = 'h-9 px-3 rounded-lg border border-[#CBD5E1] text-[13px] outline-none';

const UserCell = ({ user }) => {
  if (!user) return <span className="text-[12px] italic text-[#94A3B8]">Deleted user</span>;

  return (
    <div className="flex items-center gap-2 min-w-0">
      {user.avatarUrl ? (
        <img src={user.avatarUrl} alt="" className="w-6 h-6 rounded-full object-cover shrink-0" />
      ) : (
        <span className="w-6 h-6 rounded-full bg-[#E2E8F0] shrink-0" />
      )}
      <div className="min-w-0">
        <p className="text-[13px] font-medium text-[#334155] truncate">{user.name || user.username}</p>
        {user.username && <p className="text-[11px] text-[#94A3B8] truncate">@{user.username}</p>}
      </div>
    </div>
  );
};

const ReferralTable = ({
  rows,
  total,
  loading,
  page,
  pageSize,
  onPageChange,
  origin,
  onOriginChange,
  status,
  onStatusChange,
  search,
  onSearchChange,
  onRowClick
}) => {
  const lastPage = Math.max(0, Math.ceil(total / pageSize) - 1);

  return (
    <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden">
      <div className="px-5 py-3.5 border-b border-[#E2E8F0] bg-[#F8FAFC] flex flex-wrap items-center gap-3">
        <p className="text-[13px] font-semibold text-[#0F172A] mr-auto">Referrals</p>
        <input
          type="search"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search by name or username"
          className={`${inputClass} w-[220px]`}
        />
        <select value={origin} onChange={(e) => onOriginChange(e.target.value)} className={inputClass}>
          <option value="">All origins</option>
          {Object.entries(ORIGIN_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <select value={status} onChange={(e) => onStatusChange(e.target.value)} className={inputClass}>
          <option value="">All statuses</option>
          {Object.entries(STATUS_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="p-5 space-y-3 animate-pulse">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="h-9 w-full bg-[#F1F5F9] rounded" />
          ))}
        </div>
      ) : !rows?.length ? (
        <div className="p-8 text-center">
          <p className="text-[13px] font-medium text-[#334155]">No referrals found</p>
          <p className="text-[12px] text-[#94A3B8] mt-1">Nothing matches this filter.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="text-[11px] text-[#94A3B8] border-b border-[#E2E8F0]">
                <th className="px-5 py-2.5 font-medium">Referrer</th>
                <th className="px-3 py-2.5 font-medium">Referred</th>
                <th className="px-3 py-2.5 font-medium">Origin</th>
                <th className="px-3 py-2.5 font-medium">Module</th>
                <th className="px-3 py-2.5 font-medium">Team</th>
                <th className="px-3 py-2.5 font-medium">Progress</th>
                <th className="px-3 py-2.5 font-medium">Status</th>
                <th className="px-3 py-2.5 font-medium">Started</th>
                <th className="px-5 py-2.5 font-medium">Completed</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={`${row.module}-${row.id}`}
                  onClick={() => onRowClick(row)}
                  className="border-b border-[#F1F5F9] cursor-pointer hover:bg-[#F8FAFC]"
                >
                  <td className="px-5 py-2.5">
                    <UserCell user={row.referrer} />
                  </td>
                  <td className="px-3 py-2.5">
                    <UserCell user={row.referred} />
                  </td>
                  <td className="px-3 py-2.5 text-[13px] text-[#334155]">
                    {ORIGIN_LABELS[row.origin] || row.origin}
                  </td>
                  <td className="px-3 py-2.5 text-[13px] text-[#334155]">
                    {MODULE_LABELS[row.module]}
                    <span className="block text-[11px] text-[#94A3B8]">
                      {KIND_LABELS[row.kind] || row.kind}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-[13px] text-[#334155]">
                    {row.team ? row.team.name || row.team.username : 'Deleted team'}
                  </td>
                  <td className="px-3 py-2.5 text-[13px] text-[#334155]">
                    {row.progress.count} / {row.progress.threshold}
                  </td>
                  <td className="px-3 py-2.5 text-[13px]">
                    <span
                      className={
                        row.status === 'completed' ? 'text-[#15803D] font-medium' : 'text-[#B45309]'
                      }
                    >
                      {STATUS_LABELS[row.status]}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-[12px] text-[#64748B]">{fmtDate(row.startedAt)}</td>
                  <td className="px-5 py-2.5 text-[12px] text-[#64748B]">{fmtDate(row.completedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="px-5 py-3 flex items-center justify-between border-t border-[#E2E8F0]">
        <p className="text-[12px] text-[#94A3B8]">{total.toLocaleString()} referrals</p>
        <div className="flex items-center gap-2">
          <button
            disabled={page <= 0}
            onClick={() => onPageChange(page - 1)}
            className="h-8 px-3 rounded-lg border border-[#CBD5E1] text-[12px] disabled:opacity-40"
          >
            Previous
          </button>
          <span className="text-[12px] text-[#64748B]">
            Page {page + 1} of {lastPage + 1}
          </span>
          <button
            disabled={page >= lastPage}
            onClick={() => onPageChange(page + 1)}
            className="h-8 px-3 rounded-lg border border-[#CBD5E1] text-[12px] disabled:opacity-40"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReferralTable;